import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { StateComponent } from '../../../../../../../../common/components/state/state.component';
import { BettingResultValue, BettingTypeKey, IBetTypesConfig, IFormBettings } from '@oogaday/shared';
import { FormBettingResultModel, IFormModel } from '../../../../../../../../common/apis/form/model/form.model';
import { UtilsService } from '../../../../../../../../common/services/utils.service';
import { NamesMetadataMapping } from '../../model/model';
import { BetResultRendererComponent } from '../bet-result-renderer/bet-result-renderer.component';
import { MatIcon } from '@angular/material/icon';
import { MatList, MatListItem, MatListItemIcon, MatListItemLine, MatListItemTitle } from '@angular/material/list';
import { KeyValuePipe } from '@angular/common';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelDescription, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';

@Component({
  selector: 'oo-bet-statistics',
  templateUrl: './bet-statistics.component.html',
  styleUrls: ['./bet-statistics.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    MatList,
    MatListItem,
    MatIcon,
    MatListItemIcon,
    MatListItemTitle,
    BetResultRendererComponent,
    MatListItemLine,
    KeyValuePipe,
  ],
})
export class BetStatisticsComponent extends StateComponent<BetStatisticsComponentState> implements OnInit {
  state: BetStatisticsComponentState = {
    metadata: undefined!,
    usersBetting: undefined!,
    form: undefined!,
    bettingsMap: undefined!,
    usersMap: undefined!,
  };

  @Input() set metadata(metadata: NamesMetadataMapping) {
    this.updateState({ metadata });
  }

  @Input() set usersBetting(usersBetting: FormBettingResultModel[]) {
    this.updateState({ usersBetting });
  }

  @Input() set form(form: IFormModel) {
    this.updateState({ form });
  }

  private static calcBettingStats(mapping: BettingsMap): void {
    mapping.forEach((betting: BettingItem) => {
      const correctBets = betting.userBettings.reduce((acc, { bettingResult }) => {
        if (bettingResult === 'correct') {
          acc++;
        }

        return acc;
      }, 0);

      betting.stats = `${+((correctBets * 100) / betting.userBettings.length).toFixed(2)}%`;
    });
  }

  private static generateUsersMap(usersBetting: FormBettingResultModel[]): Map<string, User> {
    return usersBetting.reduce((usersMap, betting) => {
      if (!usersMap.has(betting.userId)) {
        usersMap.set(betting.userId, {
          name: `${betting.firstName} ${betting.lastName}`,
        });
      }

      return usersMap;
    }, new Map());
  }

  ngOnInit(): void {
    this.updateState({
      usersMap: BetStatisticsComponent.generateUsersMap(this.state.usersBetting),
      bettingsMap: this.buildMapping(this.state.form.config!.betsConfig, this.state.usersBetting),
    });
  }

  private buildMapping(betsConfig: IBetTypesConfig<any>, usersBetting: FormBettingResultModel[]): BettingsMap {
    const mapping = Object.entries(betsConfig).reduce((map: BettingsMap, [bettingType]) => {
      map.set(bettingType, {
        type: (bettingType as BettingTypeKey),
        name: UtilsService.camelToText(bettingType, true),
        userBettings: [],
      });
      usersBetting.forEach((userBetting) => {
        map.get(bettingType)!.userBettings.push({
          userId: userBetting.userId,
          betting: userBetting.betting?.[bettingType] ?? null,
          bettingResult: userBetting.bettingResults?.[bettingType],
        });
      });

      return map;
    }, new Map());

    if (this.state.form.hasBettingResult) {
      this.sortUserBetting(mapping);
      BetStatisticsComponent.calcBettingStats(mapping);
    }

    return mapping;
  }

  private sortUserBetting(mapping: BettingsMap): void {
    mapping.forEach(({ userBettings }: BettingItem) => {
      userBettings.sort((a, b) => a.bettingResult > b.bettingResult ? 1 : -1);
    });
  }
}

interface BetStatisticsComponentState {
  metadata: NamesMetadataMapping;
  usersBetting: FormBettingResultModel[];
  form: IFormModel;
  bettingsMap: BettingsMap;
  usersMap: Map<string, User>;
}

type BettingsMap = Map<string, BettingItem>;
type User = { name: string };

interface BettingItem {
  type: BettingTypeKey;
  name: string;
  userBettings: UserBettingItem[];
  stats?: string;
}

interface UserBettingItem {
  userId: string;
  betting: IFormBettings<any>;
  bettingResult: BettingResultValue;
}
